import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Clock, Filter } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { api } from '../utils/api';
import { formatDateTime } from '../utils/helpers';

const AuditLog = ({ user }) => {
  const [auditEvents, setAuditEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [actionFilter, setActionFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAuditLog();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [actionFilter, auditEvents]);

  const loadAuditLog = async () => {
    try {
      const data = await api.getAuditLog({ limit: 100 });
      setAuditEvents(data.audit_events);
      setFilteredEvents(data.audit_events);
    } catch (error) {
      console.error('Failed to load audit log:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...auditEvents];
    if (actionFilter !== 'all') {
      filtered = filtered.filter(e => e.action === actionFilter);
    }
    setFilteredEvents(filtered);
  };

  const actions = [...new Set(auditEvents.map(e => e.action))].sort();

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="audit-log-container">
      <div className="flex items-center gap-3">
        <Clock className="h-8 w-8 text-accent" />
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">Audit Trail</h1>
          <p className="text-muted-foreground mt-2">Complete history of all system actions and changes</p>
        </div>
      </div>

      <Card data-testid="filter-bar">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actions.map(action => (
                  <SelectItem key={action} value={action}>{action.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-sm text-muted-foreground ml-auto">
              Showing {filteredEvents.length} of {auditEvents.length} events
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="table-wrapper" data-testid="audit-log-table">
        <table className="w-full">
          <thead className="table-header">
            <tr className="border-b border-border">
              <th className="table-cell text-left font-medium text-muted-foreground">Timestamp</th>
              <th className="table-cell text-left font-medium text-muted-foreground">User</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Action</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Details</th>
            </tr>
          </thead>
          <tbody>
            {filteredEvents.map((event, idx) => (
              <tr key={idx} className="table-row border-b border-border" data-testid={`audit-event-row-${idx}`}>
                <td className="table-cell tabular-nums text-xs">{formatDateTime(event.timestamp)}</td>
                <td className="table-cell text-sm">{event.user}</td>
                <td className="table-cell">
                  <Badge variant="outline">{event.action.replace(/_/g, ' ')}</Badge>
                </td>
                <td className="table-cell text-sm">{event.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredEvents.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            No audit events match the current filters
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLog;
